import React from "react";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next"; // Import useTranslation
import type {
  EnrichedAttributeTransformed,
  EnrichedAttributeTermTransformed,
} from "../../types/productApiTypes";
import type { SelectedAttributeSlugs } from "../../hooks/useProductVariationSelector";
import type { RawAttribute, RawAttributeTerm } from "../../../../types";

// Helper function to safely get terms as an array
const getTermsArray = (
  terms: RawAttributeTerm[] | undefined // Expect RawAttributeTerm array
): RawAttributeTerm[] => {
  if (!terms) return [];
  return Array.isArray(terms) ? terms : [];
};

// Define the expected structure for displayInfo
export interface AttributeDisplayInfo {
  name: string; // Attribute name (from parent)
  display_type: string; // 'color', 'button-image' 'button-text'
  terms: Record<
    string, // Term Slug
    {
      color?: string;
      image?: { id: number; src: string; thumbnail: string };
    }
  >;
}

interface VariationSelectorProps {
  enrichedAttributes: EnrichedAttributeTransformed[];
  selectedAttributes: SelectedAttributeSlugs;
  onAttributeSelect: (attributeName: string, valueSlug: string) => void;
  variationsLoading?: boolean;
  containerClassName?: string;
  rowClassName?: string;
}

const VariationSelector: React.FC<VariationSelectorProps> = ({
  enrichedAttributes = [],
  selectedAttributes = {},
  onAttributeSelect,
  variationsLoading: isDataLoading = false, // Rename prop
  containerClassName = "variations-container", // Default class
  rowClassName = "variation-row", // Default class
}) => {
  const { t } = useTranslation(); // Keep for 't' function

  // Use the loading state passed from the parent directly
  const isLoading = isDataLoading;

  const variationAttributes = enrichedAttributes.filter(
    (attribute) => attribute.hasVariations
  );

  if (isLoading) {
    // Use combined loading state
    return (
      <div className={containerClassName}>
        <div className={rowClassName}>
          <Skeleton height={20} width="30%" style={{ marginBottom: "8px" }} />
          <Skeleton height={42} />
        </div>
        {variationAttributes.length > 1 && (
          <div className={rowClassName}>
            <Skeleton height={20} width="30%" style={{ marginBottom: "8px" }} />
            <Skeleton height={42} />
          </div>
        )}
      </div>
    );
  }

  if (variationAttributes.length === 0) {
    return null;
  }

  return (
    <div className={containerClassName}>
      {variationAttributes.map((attribute) => {
        const currentSelectedSlug = attribute.taxonomy
          ? selectedAttributes[attribute.taxonomy] || ""
          : "";

        const displayInfo: AttributeDisplayInfo = {
          name: attribute.name,
          display_type: attribute.displayType,
          terms: (attribute.terms || []).reduce((acc, term) => {
            acc[term.slug] = {
              color: term.color,
              image: term.buttonImage,
            };
            return acc;
          }, {} as AttributeDisplayInfo["terms"]),
        };

        const simpleAttribute: RawAttribute = {
          id: attribute.id,
          name: attribute.name,
          taxonomy: attribute.taxonomy,
          has_variations: attribute.hasVariations,
          terms: (attribute.terms || []).map(
            (term: EnrichedAttributeTermTransformed): RawAttributeTerm => ({
              id: term.id,
              name: term.name,
              slug: term.slug,
            })
          ),
        };

        const displayType = displayInfo?.display_type || "button-text";
        const terms = getTermsArray(simpleAttribute.terms);
        const termDisplayMap = displayInfo?.terms || {};

        return (
          <div key={attribute.id} className={rowClassName}>
            <label htmlFor={`variation-${attribute.name}`}>
              {t("product.attributeLabel", { name: attribute.name })}:{" "}
              {/* Translate label */}
            </label>
            {(() => {
              if (terms.length === 0) {
                return null;
              }
              switch (displayType) {
                case "color":
                  return (
                    <ColorSwatchOptions
                      attributeName={simpleAttribute.name}
                      terms={terms}
                      termDisplayData={termDisplayMap}
                      selectedValue={currentSelectedSlug}
                      onSelect={onAttributeSelect}
                    />
                  );
                case "button-text":
                  return (
                    <ButtonTextOptions
                      attributeName={simpleAttribute.name}
                      terms={terms}
                      selectedValue={currentSelectedSlug}
                      onSelect={onAttributeSelect}
                    />
                  );
                case "button-image":
                  return (
                    <ButtonImageOptions
                      attributeName={simpleAttribute.name}
                      terms={terms}
                      termDisplayData={termDisplayMap}
                      selectedValue={currentSelectedSlug}
                      onSelect={onAttributeSelect}
                    />
                  );
                default:
                  return;
              }
            })()}
          </div>
        );
      })}
    </div>
  );
};

interface ColorSwatchOptionsProps {
  attributeName: string;
  terms: RawAttributeTerm[];
  termDisplayData: Record<string, { color?: string }>;
  selectedValue: string;
  onSelect: (name: string, valueSlug: string) => void;
}

const ColorSwatchOptions: React.FC<ColorSwatchOptionsProps> = ({
  attributeName,
  terms,
  termDisplayData,
  selectedValue,
  onSelect,
}) => {
  const { t } = useTranslation(); // Get t function for sub-components
  return (
    <div className="color-swatch-container">
      <div className="color-swatch-options">
        {terms.map((term) => {
          const color = termDisplayData[term.slug]?.color || "#CCCCCC";
          const isSelected = selectedValue === term.slug;
          return (
            <button
              key={term.slug}
              type="button"
              className={`color-swatch ${isSelected ? "selected" : ""}`}
              style={{ backgroundColor: color }}
              title={term.name}
              onClick={() => onSelect(attributeName, term.slug)}
              aria-label={t("product.selectColor", { name: term.name })}
              aria-pressed={isSelected}
            >
              {isSelected && <span className="swatch-checkmark">✓</span>}
            </button>
          );
        })}
      </div>
      {selectedValue && (
        <div className="selected-attribute-label">
          {t("product.selectedLabel", {
            value:
              terms.find((t) => t.slug === selectedValue)?.name ||
              selectedValue,
          })}
        </div>
      )}
    </div>
  );
};

interface ButtonTextOptionsProps {
  attributeName: string;
  terms: RawAttributeTerm[];
  selectedValue: string;
  onSelect: (name: string, valueSlug: string) => void;
}

const ButtonTextOptions: React.FC<ButtonTextOptionsProps> = ({
  attributeName,
  terms,
  selectedValue,
  onSelect,
}) => {
  return (
    <div className="button-options-container">
      <div className="button-options">
        {terms.map((term) => {
          const isSelected = selectedValue === term.slug;
          return (
            <button
              key={term.slug}
              type="button"
              className={`button-option ${isSelected ? "selected" : ""}`}
              onClick={() => onSelect(attributeName, term.slug)}
              aria-pressed={isSelected}
            >
              {term.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};

interface ButtonImageOptionsProps {
  attributeName: string;
  terms: RawAttributeTerm[];
  termDisplayData: Record<
    string,
    { image?: { thumbnail?: string; src?: string } }
  >;
  selectedValue: string;
  onSelect: (name: string, valueSlug: string) => void;
}

const ButtonImageOptions: React.FC<ButtonImageOptionsProps> = ({
  attributeName,
  terms,
  termDisplayData,
  selectedValue,
  onSelect,
}) => {
  const { t } = useTranslation(); // Get t function
  return (
    <div className="image-options-container">
      <div className="image-options">
        {terms.map((term) => {
          const imageSrc = termDisplayData[term.slug]?.image?.thumbnail || "";
          const isSelected = selectedValue === term.slug;
          return (
            <button
              key={term.slug}
              type="button"
              className={`image-option ${isSelected ? "selected" : ""}`}
              onClick={() => onSelect(attributeName, term.slug)}
              title={term.name}
              aria-label={t("product.selectImage", { name: term.name })}
              aria-pressed={isSelected}
            >
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={term.name}
                  className="image-option-preview"
                />
              ) : (
                <span className="image-option-placeholder">
                  {term.name.charAt(0)}
                </span>
              )}
              {isSelected && <span className="option-selected-indicator" />}
            </button>
          );
        })}
      </div>
      {selectedValue && (
        <div className="selected-attribute-label">
          {t("product.selectedLabel", {
            value:
              terms.find((t) => t.slug === selectedValue)?.name ||
              selectedValue,
          })}
        </div>
      )}
    </div>
  );
};

export default VariationSelector;
